import {
  type FileMessagePartProps,
  type ImageMessagePartProps,
  MessagePrimitive,
  type ReasoningMessagePartProps,
  type TextMessagePartProps,
  ThreadPrimitive,
  type ToolCallMessagePart,
  useAuiState,
} from '@assistant-ui/react'
import { MarkdownTextPrimitive } from '@assistant-ui/react-markdown'
import { actionPath, hostFetch } from '@bridge/app-sdk'
import type { ToolArtifact } from '@web/adapter/toThreadMessages'
import { useBridge } from '@web/adapter/useBridgeRuntime'
import type { UIInteraction } from '@web/api/types'
import { Composer } from '@web/components/Composer'
import { InlineStatus } from '@web/components/InlineStatus'
import {
  MessageActorsProvider,
  MessageAvatar,
  useMessageActor,
} from '@web/components/MessageAvatar'
import {
  MessageInteraction,
  type MessageOrigin,
  MessageSendCaption,
  originKind,
  originLabel,
  systemOrigin,
} from '@web/components/MessageInteraction'
import { OpeningState } from '@web/components/OpeningState'
import { ReasoningBlock } from '@web/components/ReasoningBlock'
import { FallbackTool, TOOL_UIS } from '@web/components/tools'
import { toolState } from '@web/components/tools/ToolShell'
import { Icon } from '@web/components/ui/icons'
import { fmtTokens } from '@web/lib/format'
import type { ConversationProvenance } from '@web/state/acp'
import {
  type BuilderDecisionChoice,
  type BuilderGate,
  publishBuilderWorkflow,
  useBuilderWorkflow,
} from '@web/state/builder-workflow'
import {
  type ThoughtPresentation,
  timelineTimeLabel,
} from '@web/state/timeline'
import { describeWork } from '@web/state/working'
import { type MessageActor, readMessageActor } from 'cc-render'
import {
  createContext,
  type PropsWithChildren,
  useContext,
  useState,
} from 'react'
import remarkGfm from 'remark-gfm'

const ConversationFilter = createContext('all')
const CannedWorkflow = createContext<{
  appId?: string
  bindingId?: string | null
  workflow?: BuilderGate | null
}>({})

function ConversationLabel({
  conversation,
  inverse = false,
}: {
  conversation?: ConversationProvenance
  inverse?: boolean
}) {
  if (!conversation) return null
  return (
    <div
      className={`mb-1 text-[11px] ${inverse ? 'text-accent-fg' : 'text-muted'}`}
      title={`Conversation ${conversation.sessionId}`}
    >
      <span className="chip">{conversation.name ?? 'Subagent'}</span>
      {conversation.state
        ? ` · ${conversation.state === 'running' ? 'Working' : 'Idle'}`
        : ''}
    </div>
  )
}

export function Thread({
  user,
  app,
  bindingId,
  readOnly = false,
}: {
  user?: MessageActor
  app?: MessageActor
  bindingId?: string | null
  readOnly?: boolean
}) {
  const { snapshot } = useBridge()
  const workflow = useBuilderWorkflow(
    app?.id === 'agent-builder' && !readOnly ? bindingId : null,
  )
  const [selectedConversation, setConversation] = useState('all')
  const conversations = new Map(Object.entries(snapshot.acp.conversations))
  const selected =
    selectedConversation === 'main' || conversations.has(selectedConversation)
      ? selectedConversation
      : 'all'
  return (
    <ConversationFilter.Provider value={selected}>
      <CannedWorkflow.Provider value={{ appId: app?.id, bindingId, workflow }}>
        <MessageActorsProvider user={user} app={app}>
          <ThreadPrimitive.Root className="thread-root relative flex h-full min-w-0 flex-col">
            {snapshot.hydrating &&
              !['created', 'starting'].includes(
                snapshot.bridge.record?.status ?? 'created',
              ) && (
                <OpeningState
                  compact={snapshot.lastSeq > 0}
                  className="opening-state--thread"
                  title={
                    snapshot.lastSeq > 0
                      ? 'Updating conversation…'
                      : 'Loading saved conversation'
                  }
                  description="Restoring your messages. You can keep drafting while it finishes."
                />
              )}
            {conversations.size > 0 && (
              <label className="flex items-center gap-2 px-4 py-2 text-xs text-muted">
                Conversation
                <select
                  aria-label="Conversation"
                  className="control min-w-0"
                  value={selected}
                  onChange={(event) => setConversation(event.target.value)}
                >
                  <option value="all">All conversations</option>
                  <option value="main">Main conversation</option>
                  {[...conversations.values()].map((conversation) => (
                    <option
                      key={conversation.sessionId}
                      value={conversation.sessionId}
                    >
                      {conversation.name ??
                        `Subagent ${conversation.sessionId.slice(-8)}`}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {/* A flex column so the footer sits at the bottom of the pane when the
          thread is short, and sticks to the bottom edge while scrolling. */}
            <ThreadPrimitive.Viewport className="thread-viewport flex min-w-0 flex-1 flex-col overflow-y-auto px-4 pt-4">
              <div className="mx-auto w-full min-w-0 max-w-3xl flex-1 pb-2">
                <ThreadPrimitive.Empty>
                  <EmptyThread />
                </ThreadPrimitive.Empty>
                <ThreadPrimitive.Messages
                  components={{ UserMessage, AssistantMessage, SystemMessage }}
                />
                <WorkingIndicator />
              </div>
              <ThreadPrimitive.ViewportFooter className="sticky bottom-0 mx-auto mt-auto w-full max-w-3xl bg-app pb-3 pt-2">
                <ThreadPrimitive.ScrollToBottom className="btn btn-sm absolute -top-9 left-1/2 -translate-x-1/2 bg-elevated shadow-card disabled:hidden">
                  <Icon name="arrowDown" size={13} /> Newest
                </ThreadPrimitive.ScrollToBottom>
                {!readOnly && <Composer bindingId={bindingId} />}
              </ThreadPrimitive.ViewportFooter>
            </ThreadPrimitive.Viewport>
          </ThreadPrimitive.Root>
        </MessageActorsProvider>
      </CannedWorkflow.Provider>
    </ConversationFilter.Provider>
  )
}

/**
 * Inline "what is happening" line under the last message. Shown whenever a
 * turn is open and nothing is waiting on the user: the model is being called,
 * text is streaming, or tools are executing.
 */
function WorkingIndicator() {
  const { snapshot } = useBridge()
  const work = describeWork(
    {
      ...snapshot.acp,
      running: snapshot.acp.running || !!snapshot.optimisticWorking,
    },
    snapshot.bridge,
  )
  if (
    snapshot.hydrating ||
    ['unknown', 'created', 'starting'].includes(snapshot.bridge.status) ||
    !work.working ||
    work.tone !== 'working'
  )
    return null
  return (
    <InlineStatus icon="loader" loading testId="working-indicator">
      {work.detail}
    </InlineStatus>
  )
}

function EmptyThread() {
  const { snapshot } = useBridge()
  if (
    snapshot.hydrating ||
    ['created', 'starting', 'unknown'].includes(snapshot.bridge.status)
  )
    return null
  if (
    ['error', 'asleep', 'exited', 'closed', 'detached'].includes(
      snapshot.bridge.status,
    )
  ) {
    const work = describeWork(snapshot.acp, snapshot.bridge)
    return (
      <div className="py-16 text-center text-sm text-muted">
        <p>{work.label}</p>
        <p>{work.detail}</p>
      </div>
    )
  }
  return (
    <div className="py-16 text-center text-sm text-faint">
      Session is ready. Describe what you want done; tool calls and permission
      prompts appear inline.
    </div>
  )
}

// ------------------------------------------------------------------ parts

const MarkdownText = (_p: TextMessagePartProps) => {
  const { snapshot } = useBridge()
  const historical = useAuiState((s) =>
    snapshot.historicalMessageIds.has(s.message.id),
  )
  return (
    <MarkdownTextPrimitive
      remarkPlugins={[remarkGfm]}
      className="md text-[14px]"
      smooth={!snapshot.hydrating && !historical}
    />
  )
}

const ReasoningPart = (p: ReasoningMessagePartProps) => (
  <ReasoningBlock text={p.text} running={p.status.type === 'running'} />
)

const PlainText = (p: TextMessagePartProps) => <span>{p.text}</span>

const ImageView = (p: ImageMessagePartProps) => (
  <img
    src={p.image}
    alt=""
    className="my-1 block max-h-64 max-w-full rounded-control border border-line"
  />
)

/** Keep consecutive calls compact while ensuring pending permissions stay reachable. */
function ToolGroup({
  startIndex,
  endIndex,
  children,
}: PropsWithChildren<{ startIndex: number; endIndex: number }>) {
  const parts = useAuiState((s) => s.message.content)
  const { snapshot } = useBridge()
  const turnOpen = snapshot.acp.running
  const tools = parts
    .slice(startIndex, endIndex + 1)
    .filter((p): p is ToolCallMessagePart => p.type === 'tool-call')
  const [override, setOverride] = useState<boolean | null>(null)
  if (tools.length < 2) return <>{children}</>

  const states = tools.map((tool) =>
    toolState(
      tool.artifact as ToolArtifact | undefined,
      tool.result,
      tool.isError,
      turnOpen,
    ),
  )
  const active = states.some((state) =>
    ['approval', 'pending', 'running'].includes(state),
  )
  const failed = states.filter(
    (state) => state === 'error' || state === 'denied',
  ).length
  const interrupted = states.filter((state) => state === 'cancelled').length
  const commands = tools.filter((t) => t.toolName === 'Bash').length
  const others = tools.length - commands
  const label = [
    commands
      ? `${active ? 'Running' : 'Ran'} ${commands} command${commands === 1 ? '' : 's'}`
      : '',
    others
      ? `${active ? 'Using' : commands ? 'used' : 'Used'} ${others} tool${others === 1 ? '' : 's'}`
      : '',
  ]
    .filter(Boolean)
    .join(', ')
  const open = states.includes('approval') || (override ?? false)

  return (
    <div className="my-1.5">
      <button
        className={`flex w-full items-center gap-2.5 rounded-control border px-3 py-2 text-left text-[13px] font-medium transition-colors ${
          open
            ? 'border-accent/50 bg-accent-soft text-accent'
            : 'border-accent/40 bg-accent-soft/70 text-accent hover:bg-accent-soft'
        }`}
        onClick={() => setOverride(!open)}
        aria-expanded={open}
      >
        <Icon name="magic" size={14} className={active ? 'pulse' : ''} />
        <span className="min-w-0 flex-1 truncate">
          {label}
          {active ? '…' : ''}
          {failed ? (
            <span className="text-danger"> · {failed} failed</span>
          ) : null}
          {interrupted ? (
            <span className="text-muted"> · {interrupted} interrupted</span>
          ) : null}
        </span>
        <Icon name={open ? 'chevronDown' : 'chevronRight'} size={13} />
      </button>
      {open && <div className="mt-1.5">{children}</div>}
    </div>
  )
}

/**
 * Delivery receipt for a user message, always the same size so the bubble
 * does not shift: a grey check until the server accepts the message,
 * a check once it has, a faint mark if the session ended without it.
 */
function Receipt({
  delivered,
  sessionOver,
  inverse = false,
}: {
  delivered: boolean
  sessionOver: boolean
  inverse?: boolean
}) {
  const { snapshot } = useBridge()
  const historical = useAuiState((s) =>
    snapshot.historicalMessageIds.has(s.message.id),
  )
  if (!delivered && (snapshot.hydrating || historical))
    return (
      <Icon
        name="noResult"
        size={11}
        className={inverse ? 'text-accent-fg' : 'text-faint'}
        title="Saved message"
      />
    )
  if (delivered)
    return (
      <Icon
        name="receipt"
        size={11}
        className={inverse ? 'text-accent-fg' : 'text-accent'}
        title="Received by server"
      />
    )
  if (sessionOver)
    return (
      <Icon
        name="noResult"
        size={11}
        className={inverse ? 'text-accent-fg' : 'text-faint'}
        title="Not received: the session ended first"
      />
    )
  return (
    <Icon
      name="receipt"
      size={11}
      className={inverse ? 'text-accent-fg' : 'text-faint'}
      title="Sending…"
      still
    />
  )
}

// --------------------------------------------------------------- messages

const FileView = (part: FileMessagePartProps) => {
  const src = `data:${part.mimeType};base64,${part.data}`
  return part.mimeType.startsWith('audio/') ? (
    // biome-ignore lint/a11y/useMediaCaption: ACP audio has no caption track; text content remains alongside it.
    <audio controls src={src} className="max-w-full" />
  ) : (
    <a
      className="text-accent underline"
      href={src}
      download={part.filename ?? 'attachment'}
    >
      Download {part.filename ?? 'attachment'}
    </a>
  )
}

const PART_COMPONENTS = {
  Text: MarkdownText,
  Reasoning: ReasoningPart,
  Image: ImageView,
  File: FileView,
  tools: { by_name: TOOL_UIS, Fallback: FallbackTool },
  ToolGroup,
}

/** Match a Builder gate when its question closes an assistant response. */
function closesWithWorkflowGate(text: string, question: string) {
  return text
    .trimEnd()
    .toLocaleLowerCase()
    .endsWith(question.toLocaleLowerCase())
}

/** Render cached Builder gates as UI-owned choices rather than plain chat text. */
function CannedWorkflowChoices({ text }: { text: string }) {
  const { appId, bindingId, workflow } = useContext(CannedWorkflow)
  const latest = useAuiState((state) => state.message.isLast)
  const { send, reportError } = useBridge()
  const [pending, setPending] = useState<string | null>(null)
  const [submitted, setSubmitted] = useState<string | null>(null)
  const [error, setError] = useState('')
  const gate = workflow?.gate
  if (
    appId !== 'agent-builder' ||
    !bindingId ||
    !gate ||
    !closesWithWorkflowGate(text, gate.question) ||
    !latest ||
    submitted !== null ||
    workflow.progress ||
    (gate.kind === 'approval' && !workflow.plan)
  )
    return null

  const choose = async (choice: BuilderDecisionChoice) => {
    setPending(choice.id)
    setError('')
    try {
      const state = await hostFetch(
        `/v1/apps/bindings/${encodeURIComponent(bindingId)}/state`,
      )
      const revision = state?.workflow?.revision
      if (!Number.isInteger(revision))
        throw new Error('The current workflow decision is unavailable.')
      publishBuilderWorkflow(bindingId, state.workflow)
      if (
        state.workflow.gate?.kind !== gate.kind ||
        state.workflow.gate?.question !== gate.question ||
        revision !== workflow.revision ||
        state.workflow.progress
      )
        throw new Error(
          'The workflow changed. Review the updated plan before deciding.',
        )
      const invocationId = crypto.randomUUID()
      const result = await hostFetch(
        actionPath(bindingId, 'user_apps.decision'),
        {
          input: { revision, choice: choice.id },
          invocationId,
          idempotencyKey: invocationId,
        },
      )
      setSubmitted(choice.id)
      if (result?.data?.workflow)
        publishBuilderWorkflow(bindingId, result.data.workflow)
      const previewUrl = result?.data?.workflow?.preview_url
      if (choice.id === 'preview' && typeof previewUrl === 'string') {
        window.location.assign(previewUrl)
        return
      }
      if (!send({ type: 'prompt', text: choice.message }))
        reportError('Your decision was saved, but the conversation is offline.')
    } catch (failure) {
      setError(
        failure instanceof Error
          ? failure.message
          : 'The decision could not be saved.',
      )
    } finally {
      setPending(null)
    }
  }

  return (
    <fieldset
      className="mt-3"
      disabled={pending !== null || submitted !== null}
    >
      <legend className="text-[11px] font-medium text-muted">
        {gate.question}
      </legend>
      <div
        className="mt-1.5 flex flex-wrap gap-2"
        role="group"
        aria-label={gate.question}
      >
        {gate.choices.map((choice) => (
          <button
            key={choice.id}
            type="button"
            className={choice.primary ? 'btn btn-primary btn-sm' : 'btn btn-sm'}
            onClick={() => void choose(choice)}
          >
            {pending === choice.id ? 'Saving…' : choice.label}
          </button>
        ))}
      </div>
      {error && (
        <p role="alert" className="mt-2 text-[11px] text-danger">
          {error}
        </p>
      )}
    </fieldset>
  )
}

/**
 * A turn arrives as alternating text and tool segments (see
 * toThreadMessages). Text segments get a left-aligned bubble; tool segments
 * stand between bubbles as bare cards, folded by ToolGroup when there are
 * several.
 */
function AssistantMessage() {
  const selectedConversation = useContext(ConversationFilter)
  const status = useAuiState((s) =>
    s.message.role === 'assistant' ? s.message.status : undefined,
  )
  const custom = useAuiState((s) => s.message.metadata.custom) as
    | {
        actor?: MessageActor
        kind?: 'text' | 'tools' | 'reasoning'
        thought?: ThoughtPresentation
        conversation?: ConversationProvenance
      }
    | undefined
  const actor = useMessageActor('assistant', readMessageActor(custom?.actor))
  // A streaming entry exists before any token has arrived, and a text block
  // starts empty. No bubble until there is something to show; the working
  // indicator under the thread covers the wait.
  const hasVisible = useAuiState((s) =>
    s.message.content.some((p) =>
      p.type === 'text' || p.type === 'reasoning'
        ? p.text.trim().length > 0
        : p.type !== 'tool-call',
    ),
  )
  const footer = (
    <>
      {status?.type === 'incomplete' && status.reason === 'cancelled' && (
        <div className="mt-1 text-[11px] text-warn">Interrupted.</div>
      )}
      {status?.type === 'incomplete' && status.reason === 'error' && (
        <div role="alert" className="mt-1 break-words text-[12px] text-danger">
          {String(status.error ?? 'Error')}
        </div>
      )}
    </>
  )
  const text = useAuiState((s) =>
    s.message.content
      .map((part) => (part.type === 'text' ? part.text : ''))
      .join(''),
  )
  if (
    selectedConversation !== 'all' &&
    selectedConversation !== (custom?.conversation?.sessionId ?? 'main')
  )
    return null
  if (custom?.kind === 'tools') {
    return (
      <MessagePrimitive.Root
        data-conversation={custom.conversation?.sessionId}
        className="ml-[42px] max-w-[calc(94%-42px)] py-0.5"
      >
        <ConversationLabel conversation={custom.conversation} />
        <MessagePrimitive.Parts components={PART_COMPONENTS} />
        {footer}
      </MessagePrimitive.Root>
    )
  }
  if (custom?.kind === 'reasoning' && custom.thought) {
    return (
      <MessagePrimitive.Root
        className="thought-timeline-entry"
        data-conversation={custom.conversation?.sessionId}
      >
        <ConversationLabel conversation={custom.conversation} />
        <ReasoningBlock
          text=""
          traces={custom.thought.traces}
          latest={custom.thought.latest}
          running={status?.type === 'running'}
        />
        {footer}
      </MessagePrimitive.Root>
    )
  }
  if (!hasVisible && !(status?.type === 'incomplete')) return null
  return (
    <MessagePrimitive.Root
      data-conversation={custom?.conversation?.sessionId}
      className={`flex min-w-0 items-start justify-start gap-2.5 py-1.5 ${custom?.conversation ? 'ml-4 border-l-2 border-line pl-2' : ''}`}
    >
      <MessageAvatar actor={actor} />
      {/* Left-aligned bubble, the counterpart of the user's right-aligned one. */}
      <div className="min-w-0 max-w-[calc(94%-42px)] rounded-card rounded-tl-sm border border-line bg-elevated px-4 py-2.5 shadow-sm">
        {custom?.actor && (
          <div className="mb-1 text-[11px] text-muted">{actor.name}</div>
        )}
        <ConversationLabel conversation={custom?.conversation} />
        <MessagePrimitive.Parts components={PART_COMPONENTS} />
        {status?.type !== 'running' && <CannedWorkflowChoices text={text} />}
        {footer}
      </div>
    </MessagePrimitive.Root>
  )
}

function UserMessage() {
  const selectedConversation = useContext(ConversationFilter)
  const hasAttachment = useAuiState((s) =>
    s.message.content.some(
      (part) => part.type === 'image' || part.type === 'file',
    ),
  )
  const custom = useAuiState((s) => s.message.metadata.custom) as
    | {
        actor?: MessageActor
        uuid?: string
        synthetic?: boolean
        sendMode?: import('@web/api/types').MessageSendMode
        origin?: MessageOrigin
        interaction?: UIInteraction
        delivered?: boolean
        pending?: boolean
        isReplay?: boolean
        conversation?: ConversationProvenance
      }
    | undefined
  const { send, snapshot } = useBridge()
  const sessionOver = [
    'exited',
    'error',
    'detached',
    'asleep',
    'closed',
  ].includes(snapshot.bridge.status)
  const kind = originKind(custom?.origin)
  const fromSystem = systemOrigin(custom?.origin) || custom?.synthetic
  const actor = useMessageActor(
    'user',
    fromSystem
      ? {
          kind: 'system',
          id: kind ?? 'injected',
          name: custom?.synthetic
            ? 'Injected context'
            : originLabel(custom?.origin),
        }
      : readMessageActor(custom?.actor),
  )
  if (
    selectedConversation !== 'all' &&
    selectedConversation !== (custom?.conversation?.sessionId ?? 'main')
  )
    return null
  return (
    <MessagePrimitive.Root
      data-conversation={custom?.conversation?.sessionId}
      className="group flex items-start justify-end gap-2 py-2"
    >
      <div className="min-w-0 max-w-[85%]">
        <div
          className={`whitespace-pre-wrap break-words rounded-card rounded-tr-sm px-4 py-2.5 text-[14px] ${
            fromSystem
              ? 'border border-line bg-inset'
              : 'bg-message-accent text-accent-fg text-balance'
          }`}
        >
          {custom?.actor && (
            <div
              className={`mb-1 text-[11px] ${fromSystem ? 'text-muted' : 'text-accent-fg'}`}
            >
              {actor.name}
            </div>
          )}
          <ConversationLabel
            conversation={custom?.conversation}
            inverse={!fromSystem}
          />
          <MessageInteraction
            interaction={custom?.interaction}
            origin={custom?.origin}
            synthetic={custom?.synthetic}
            accent={!fromSystem}
          />
          <MessagePrimitive.Parts
            components={{ Text: PlainText, Image: ImageView, File: FileView }}
          />
          <span
            className={
              hasAttachment
                ? 'mt-1 flex justify-end text-[10.5px] text-faint'
                : 'ml-3 inline-flex items-center gap-1 whitespace-nowrap align-baseline text-[10.5px] text-faint'
            }
          >
            <Receipt
              delivered={custom?.delivered ?? !!custom?.isReplay}
              sessionOver={sessionOver && !custom?.pending}
              inverse={!fromSystem}
            />
          </span>
        </div>
        <MessageSendCaption
          mode={custom?.sendMode}
          origin={custom?.origin}
          synthetic={custom?.synthetic}
        />
      </div>
      <MessageAvatar actor={actor} />
    </MessagePrimitive.Root>
  )
}

function SystemMessage() {
  const custom = useAuiState((s) => s.message.metadata.custom) as
    | {
        kind?: string
        variant?: string
        detail?: Record<string, unknown> | null
        loading?: boolean
        failed?: boolean
        lifecycleDetail?: string
        lifecycleTimestamp?: number
        timestamp?: number
        conversation?: ConversationProvenance
        startupPhase?: string
      }
    | undefined
  const text = useAuiState((s) =>
    s.message.content.map((p) => (p.type === 'text' ? p.text : '')).join(''),
  )
  const selectedConversation = useContext(ConversationFilter)
  if (custom?.kind === 'time-separator' && custom.timestamp) {
    if (
      selectedConversation !== 'all' &&
      selectedConversation !== (custom.conversation?.sessionId ?? 'main')
    )
      return null
    return (
      <MessagePrimitive.Root className="timeline-time-separator">
        <time dateTime={new Date(custom.timestamp * 1000).toISOString()}>
          {timelineTimeLabel(custom.timestamp)}
        </time>
      </MessagePrimitive.Root>
    )
  }
  if (custom?.kind === 'lifecycle') {
    return (
      <MessagePrimitive.Root
        className={`session-lifecycle-entry ${custom.loading ? 'is-loading' : ''} ${custom.failed ? 'is-error' : ''}`}
        data-testid={custom.loading ? 'startup-messages' : undefined}
        data-startup-phase={custom.startupPhase}
        data-startup-state={custom.loading ? 'current' : undefined}
      >
        <InlineStatus
          icon={custom.loading ? 'loader' : custom.failed ? 'warning' : 'check'}
          loading={custom.loading}
          failed={custom.failed}
        >
          <div className="font-medium">{text}</div>
          {custom.lifecycleDetail && (
            <p className="mt-1 text-xs text-muted">{custom.lifecycleDetail}</p>
          )}
        </InlineStatus>
      </MessagePrimitive.Root>
    )
  }
  if (custom?.kind === 'divider') {
    const d = custom.detail ?? {}
    const extra =
      custom.variant === 'compaction' && d.preTokens != null
        ? ` · ${fmtTokens(Number(d.preTokens))} → ${fmtTokens(Number(d.postTokens))} tokens`
        : ''
    return (
      <MessagePrimitive.Root className="flex items-center gap-3 py-3 text-[11px] text-faint">
        <span className="h-px flex-1 bg-line" />
        <span>
          {text}
          {extra}
        </span>
        <span className="h-px flex-1 bg-line" />
      </MessagePrimitive.Root>
    )
  }
  // Output of a local slash command (/model, /compact, ...).
  return (
    <MessagePrimitive.Root className="flex justify-center py-1.5">
      <span className="mono inline-flex max-w-full items-center gap-1.5 rounded-pill border border-line bg-inset px-3 py-1 text-[11.5px] text-muted">
        <Icon name="terminal" size={11} className="shrink-0 text-faint" />
        <span className="truncate whitespace-pre-wrap">{text}</span>
      </span>
    </MessagePrimitive.Root>
  )
}
